import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';

import { MESSAGE_CONFIG, NOTIFICATION_TYPE } from 'enum/messages';

import { FirebaseMain } from '../firebase';
import firebaseNode from '../firebaseNode';
import { CHAT_MESSAGE_LABELS } from '../firebaseStructure';
import {
  accessLevelFromShortName,
  genderFromShortName,
} from '../firebaseTranscribers.js';


const ADD_MAIN_CHAT_MESSAGE = 'cam4/firebaseWatchers/mainChatState/ADD_MAIN_CHAT_MESSAGE';
const RESET_STATE = 'cam4/firebaseWatchers/mainChatState/RESET_STATE';
const SET_INITIAL_MAIN_CHAT_MESSAGES = 'cam4/firebaseWatchers/mainChatState/SET_INITIAL_MAIN_CHAT_MESSAGES';

export const NAME = 'mainChat';

/*
INITIAL STATE
*/

const initialState = Immutable.fromJS({
  messages: [],
  initialMessagesSet: false,
});

/*
REDUCER
*/
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_INITIAL_MAIN_CHAT_MESSAGES:
      return state.set('messages', action.messages).set('initialMessagesSet', true);

    case ADD_MAIN_CHAT_MESSAGE:
      return state.set('messages', [...state.get('messages'), action.message]);

    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

/*
INPUT SELECTORS
*/

const messages = (state) => state[NAME].get('messages');
const initialMessagesSet = (state) => state[NAME].get('initialMessagesSet');

/*
ACTIONS
*/

function populateInitialMessageHistory(messages) {
  return {
    type: SET_INITIAL_MAIN_CHAT_MESSAGES,
    messages,
  };
}

export function addMessage(message) {
  return {
    type: ADD_MAIN_CHAT_MESSAGE,
    message,
  };
}

function stateToInitialValues() {
  return { type: RESET_STATE };
}

/*
HELPERS
*/

function parseHistoryMessage(key, messageObject) {
  const parsedMessage = {
    messageKey: `${key}${messageObject[CHAT_MESSAGE_LABELS.TIMESTAMP]}`,
    message: messageObject[CHAT_MESSAGE_LABELS.MESSAGE],
    senderOriginalUsername: messageObject[CHAT_MESSAGE_LABELS.ORIGINAL_USERNAME],
    gender: genderFromShortName(messageObject[CHAT_MESSAGE_LABELS.GENDER]),
    language: messageObject[CHAT_MESSAGE_LABELS.LANGUAGE],

    // Get type of message (if undefined - normal chat message and set to 1,
    // else notification message)
    type: typeof messageObject[CHAT_MESSAGE_LABELS.TYPE] === 'undefined' ? 1 : messageObject[CHAT_MESSAGE_LABELS.TYPE],

    // Depending on type of message it has extra info (tokens tipped, gift id, etc)
    notificationExtra:
      messageObject[CHAT_MESSAGE_LABELS.TOKENS] ||
      messageObject[CHAT_MESSAGE_LABELS.GIFT_ID] ||
      messageObject[CHAT_MESSAGE_LABELS.FANCLUB] ||
      messageObject[CHAT_MESSAGE_LABELS.GAME_RESULT],

    // Anonymous setting for tipping notifications
    anonymous: messageObject[CHAT_MESSAGE_LABELS.ANONYMOUS] || 0,
    senderAccessLevel: accessLevelFromShortName(messageObject[CHAT_MESSAGE_LABELS.ACCESS_LEVEL]),
    isFanclubSubscribed: messageObject[CHAT_MESSAGE_LABELS.FANCLUB] === '1',
    isModeratorValue: messageObject[CHAT_MESSAGE_LABELS.IS_ROOM_MODERATOR],
    senderIsRoomModerator: messageObject[CHAT_MESSAGE_LABELS.IS_ROOM_MODERATOR] === '1',

    viewerType: messageObject[CHAT_MESSAGE_LABELS.VIEWER_TYPE] || 0,
    timestamp: messageObject[CHAT_MESSAGE_LABELS.TIMESTAMP] || Date.now(),
    isBroadcasting: messageObject[CHAT_MESSAGE_LABELS.IS_BROADCASTING] === '1',
    isGoalReached: messageObject[CHAT_MESSAGE_LABELS.TIP_TO_REACH_GOAL] === '1',
    isHistoryMessage: true,
  };

  if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_PURCHASE) {
    parsedMessage.videoTitle = messageObject[CHAT_MESSAGE_LABELS.VIDEO_TITLE];
    parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
  }
  if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_BULK_PURCHASE) {
    parsedMessage.videoCount = messageObject[CHAT_MESSAGE_LABELS.VIDEO_COUNT];
    parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
  }
  if (parsedMessage.type === NOTIFICATION_TYPE.GIFT) {
    parsedMessage.giftImage = messageObject[CHAT_MESSAGE_LABELS.GIFT_IMAGE];
    parsedMessage.giftValue = messageObject[CHAT_MESSAGE_LABELS.GIFT_VALUE];
  }

  let messageHash = '';
  if (CHAT_MESSAGE_LABELS.MESSAGE_HASH in messageObject) {
    messageHash = messageObject[CHAT_MESSAGE_LABELS.MESSAGE_HASH];
  }
  parsedMessage.hash = messageHash;

  return parsedMessage;
}

function parseLightMessage(messageObject) {
  const properties = messageObject[CHAT_MESSAGE_LABELS.PROPERTIES];
  const translatorHashLength = 6;
  let parsedMessage = undefined;

  if (typeof properties !== typeof undefined) {
    const offset = properties.charAt(1) !== '0' && properties.charAt(1) !== '1' ? 1 : 0;

    const firstDelimiter = properties.indexOf('$');
    const secondDelimiter = properties.indexOf('$', firstDelimiter + 1);
    const thirdDelimiter = properties.indexOf('$', secondDelimiter + 1);

    let timestamp = undefined;
    if (thirdDelimiter > 0) {
      timestamp = properties.substring(secondDelimiter + 1, thirdDelimiter);
    } else {
      timestamp = properties.substring(secondDelimiter + 1);
    }

    // regular message
    parsedMessage = {
      message: messageObject[CHAT_MESSAGE_LABELS.MESSAGE],
      senderAccessLevel: accessLevelFromShortName(properties.substring(0, 1 + offset)),
      senderIsRoomModerator: properties.charAt(1 + offset) === '1',
      isFanclubSubscribed: properties.charAt(2 + offset) === '1',
      gender: genderFromShortName(properties.charAt(3 + offset)),
      language: properties.substring(4 + offset, 6 + offset),
      viewerType: properties.charAt(6 + offset),
      hash: properties.substring(firstDelimiter - translatorHashLength, firstDelimiter),
      senderOriginalUsername: properties.substring(firstDelimiter + 1, secondDelimiter),
      isBroadcasting: thirdDelimiter > 0 ? properties.charAt(thirdDelimiter + 1) === '1' : 1,
      timestamp,
      type: 1,
      isHistoryMessage: false,
    };
  } else {
    // notification
    parsedMessage = {
      senderOriginalUsername: messageObject[CHAT_MESSAGE_LABELS.ORIGINAL_USERNAME],
      type: messageObject[CHAT_MESSAGE_LABELS.TYPE],
      // Depending on type of message it has extra info (tokens tipped, gift id, etc)
      notificationExtra:
        messageObject[CHAT_MESSAGE_LABELS.TOKENS] ||
        messageObject[CHAT_MESSAGE_LABELS.GIFT_ID] ||
        messageObject[CHAT_MESSAGE_LABELS.FANCLUB] ||
        messageObject[CHAT_MESSAGE_LABELS.GAME_RESULT],
      // Anonymous setting for tipping notifications
      anonymous: messageObject[CHAT_MESSAGE_LABELS.ANONYMOUS] === '1',
      timestamp: messageObject[CHAT_MESSAGE_LABELS.TIMESTAMP] || Date.now(),
      isGoalReached: messageObject[CHAT_MESSAGE_LABELS.TIP_TO_REACH_GOAL] === '1',
      // Adding access level
      senderAccessLevel: accessLevelFromShortName(messageObject[CHAT_MESSAGE_LABELS.ACCESS_LEVEL]),
      isHistoryMessage: false,
    };

    if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_PURCHASE) {
      parsedMessage.videoTitle = messageObject[CHAT_MESSAGE_LABELS.VIDEO_TITLE];
      parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
    }
    if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_BULK_PURCHASE) {
      parsedMessage.videoCount = messageObject[CHAT_MESSAGE_LABELS.VIDEO_COUNT];
      parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
    }
    if (parsedMessage.type === NOTIFICATION_TYPE.GIFT) {
      parsedMessage.giftImage = messageObject[CHAT_MESSAGE_LABELS.GIFT_IMAGE];
      parsedMessage.giftValue = messageObject[CHAT_MESSAGE_LABELS.GIFT_VALUE];
    }
  }

  parsedMessage.messageKey = `chat_message_${parsedMessage.timestamp}`;

  return parsedMessage;
}

/*
WATCHERS
*/

function watchMainChat(roomName) {
  const mainChatRef = FirebaseMain.child(`${firebaseNode.CHAT_ROOMS}/${roomName}/chatMessages`);
  const mainChatLightRef = FirebaseMain.child(`c/${roomName}`);

  return (dispatch) => {
    // get last n messages from message history
    mainChatRef
      .limitToLast(MESSAGE_CONFIG.HISTORY_LIMIT)
      .once('value', (snapshot) => {
        const messages = snapshot.val();
        if (messages) {
          const messageKey = snapshot.key();
          const parsedHistoryMessages = Object.keys(snapshot.val()).map((messageHash) =>
            parseHistoryMessage(messageKey, messages[messageHash])
          );
          dispatch(populateInitialMessageHistory(parsedHistoryMessages));
        }
      })
      .then(() => {
        // skip first message since it is duplicate of last message from history
        let skipValue = true;

        mainChatLightRef.on('value', (snapshot) => {
          const message = snapshot.val();
          if (message) {
            const parsedLightMessage = parseLightMessage(snapshot.val());
            if (!skipValue) {
              dispatch(addMessage(parsedLightMessage));
            }
          }
          skipValue = false;
        });
      });
  };
}

// revert state to initial values
function resetMainChat() {
  return (dispatch) => {
    dispatch(stateToInitialValues());
  };
}

/*
EXPORTS
*/

export const selector = createStructuredSelector({
  mainChatData: createStructuredSelector({
    messages,
    initialMessagesSet,
  }),
});

export const actionCreators = {
  watchMainChat,
  resetMainChat,
  addMessage,
};
